import { memo, useState, useEffect, useRef } from 'react';
import useApi from '../../api';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';

interface FormField {
    name: string;
    type: 'text' | 'dropdown';
    label: string;
    options?: string[];
    value: string;
}

interface FormFieldProps {
    fields: FormField[];
    onSubmit: (fieldName: string, value: string) => void;
    conversationId: string;
    config: any;
    gettingFullResponse: boolean;
    onUpdate?: (fieldName: string, value: string) => void;
    setLeadId?: (leadId: string) => void;
    parentUrl?: string;
}

interface UpdateOperation {
    fieldName: string;
    value: string;
    timestamp: number;
}

const FormField = memo(({ 
    fields,
    onSubmit, 
    conversationId, 
    config,
    gettingFullResponse,
    onUpdate,
    setLeadId,
    parentUrl
}: FormFieldProps) => {
    const [formValues, setFormValues] = useState<Record<string, string>>(
        fields.reduce((acc, field) => ({
            ...acc,
            [field.name]: field.value
        }), {})
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingField, setEditingField] = useState<string | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [loadingOption, setLoadingOption] = useState<string | null>(null);
    const { post } = useApi();

    const [pendingUpdates, setPendingUpdates] = useState<UpdateOperation[]>([]);
    const [isProcessingQueue, setIsProcessingQueue] = useState(false);
    const updateInProgress = useRef<boolean>(false);

    useEffect(() => {
        setFormValues(prev => {
            const newValues = { ...prev };
            fields.forEach(field => {
                if (!newValues[field.name]) {
                    newValues[field.name] = field.value;
                }
            });
            return newValues;
        });
    }, [fields]);

    useEffect(() => {
        const processQueue = async () => {
            if (pendingUpdates.length === 0 || isProcessingQueue || updateInProgress.current) return;
            
            setIsProcessingQueue(true);
            updateInProgress.current = true;
            
            const update = pendingUpdates[0];
            
            try {
                const success = await handleFieldUpdate(update.fieldName, update.value);
                if (success) {
                    setPendingUpdates(current => current.slice(1));
                }
            } finally {
                setIsProcessingQueue(false);
                updateInProgress.current = false;
            }
        };

        processQueue();
    }, [pendingUpdates, isProcessingQueue]);

    const queueFieldUpdate = (fieldName: string, value: string) => {
        setPendingUpdates(current => [...current, {
            fieldName,
            value,
            timestamp: Date.now()
        }]);
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleFieldUpdate = async (fieldName: string, value: string) => {
        if (!value.trim()) {
            setEditingField(null);
            return false;
        }

        setIsSubmitting(true);
        try {
            const newFormValues = { ...formValues, [fieldName]: value };

            if (fieldName === 'email' && !validateEmail(value)) {
                toast.error('Please enter a valid email address', toastConfig);
                return false;
            }

            const response = await post(`/sessions/${conversationId}/update-form/`, {
                form_data: newFormValues,
                parent_url: parentUrl
            });

            const leadId: string | undefined = response.data.lead_id;
            if (leadId && setLeadId) {
                try {
                    setLeadId(leadId);
                } catch (error) {
                    console.error('Error setting lead ID:', error);
                }
            }
            
            setFormValues(newFormValues);
            onUpdate?.(fieldName, value);
            setEditingField(null);

            const isFormComplete = fields.every(field =>
                field.name === fieldName ? value.trim() !== '' : newFormValues[field.name]?.trim() !== ''
            );

            if (isFormComplete && !hasSubmitted) {
                setHasSubmitted(true);
                onSubmit(fieldName, value);
            }

            return true;
        } catch (error) {
            console.error('API call failed:', error);
            setFormValues(prev => ({ ...prev, [fieldName]: prev[fieldName] }));
            toast.error('Failed to update field. Please try again.', toastConfig);
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    const hexToRgb = (hex: string) => {
        hex = hex.replace(/^#/, '');
        let bigint = parseInt(hex, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;
        return `${r}, ${g}, ${b}`;
    };

    const handleInputBlur = async (field: FormField, value: string) => {
        if (value.trim() && !isSubmitting) {
            queueFieldUpdate(field.name, value);
            
            const newValues = { ...formValues, [field.name]: value.trim() };
            const willBeComplete = fields.every(f => 
                f.name === field.name ? value.trim() !== '' : newValues[f.name]?.trim() !== ''
            );

            if (!willBeComplete) {
                const currentIndex = fields.findIndex(f => f.name === field.name);
                const nextEmptyField = fields.slice(currentIndex + 1).find(f => 
                    !formValues[f.name]?.trim()
                );
                if (nextEmptyField) {
                    setEditingField(nextEmptyField.name);
                }
            } else {
                setEditingField(null);
            }
        }
    };

    const handleInputKeyDown = async (e: React.KeyboardEvent, field: FormField, value: string) => {
        if (e.key === 'Enter' && value.trim() && !isSubmitting) {
            e.preventDefault();
            queueFieldUpdate(field.name, value);
            
            const newValues = { ...formValues, [field.name]: value.trim() };
            const willBeComplete = fields.every(f => 
                f.name === field.name ? value.trim() !== '' : newValues[f.name]?.trim() !== ''
            );

            if (!willBeComplete) {
                const currentIndex = fields.findIndex(f => f.name === field.name);
                const nextEmptyField = fields.slice(currentIndex + 1).find(f => 
                    !formValues[f.name]?.trim()
                );
                if (nextEmptyField) {
                    setEditingField(nextEmptyField.name);
                }
            } else {
                setEditingField(null);
            }
        }
    };

    const handleDropdownClick = async (fieldName: string, option: string) => {
        setLoadingOption(option);
        const newValues = {
            ...formValues,
            [fieldName]: option
        };
        setFormValues(newValues);
        
        while (updateInProgress.current) {
            await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        try {
            queueFieldUpdate(fieldName, option);
        } finally {
            setLoadingOption(null);
        }
    };

    if (gettingFullResponse) return null;

    const firstEmptyField = fields.find(field => !formValues[field.name]);

    return (
        <div className="my-4 space-y-4 font-inherit">
            {fields.map((field) => {
                const value = formValues[field.name] || '';
                const isFieldFilled = value.trim() !== '';
                const shouldAutoFocus = field.name === firstEmptyField?.name;
                const isEditing = editingField === field.name;

                if (isFieldFilled && !isEditing) {
                    return (
                        <div 
                            key={field.name}
                            className="flex items-center space-x-2 my-2 p-2 rounded-lg w-full font-inherit cursor-pointer hover:bg-opacity-10"
                            onClick={() => setEditingField(field.name)}
                            style={{
                                backgroundColor: `rgba(${hexToRgb(config.color)}, 0.03)`,
                                border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                color: `rgba(${hexToRgb(config.color)}, 0.7)`
                            }}
                        >
                            <span className="text-green-500 flex-shrink-0">✓</span>
                            <div className="flex flex-col flex-1 min-w-0">
                                <span className="text-sm font-semibold text-slate-600 whitespace-pre-wrap">{field.label}</span>
                                <span className="text-sm text-slate-600 whitespace-pre-wrap">{value}</span>
                            </div>
                            <span className="text-xs text-slate-400">Click to edit</span>
                        </div>
                    );
                }

                return (
                    <div key={field.name} className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-600">
                            {field.label}
                        </label>
                        {field.type === 'dropdown' ? (
                            <div className="flex flex-wrap gap-2">
                                {field.options?.map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => handleDropdownClick(field.name, option)}
                                        className="px-4 py-2 rounded-full text-sm border transition-transform duration-200 font-inherit hover:scale-105"
                                        style={{
                                            backgroundColor: value === option 
                                                ? `rgba(${hexToRgb(config.color)}, 0.1)`
                                                : 'white',
                                            borderColor: value === option 
                                                ? `rgba(${hexToRgb(config.color)}, 0.2)`
                                                : 'rgb(226, 232, 240)',
                                            color: value === option 
                                                ? `rgba(${hexToRgb(config.color)}, 0.8)`
                                                : 'rgb(71, 85, 105)'
                                        }}
                                    >
                                        {loadingOption === option ? (
                                            <svg 
                                                className="animate-spin h-4 w-4 mx-auto" 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24"
                                                style={{
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <circle 
                                                    className="opacity-25" 
                                                    cx="12" cy="12" 
                                                    r="10" 
                                                    stroke="currentColor" 
                                                    strokeWidth="4"
                                                ></circle>
                                                <path 
                                                    className="opacity-75" 
                                                    fill="currentColor" 
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                        ) : option}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => setFormValues(prev => ({
                                        ...prev,
                                        [field.name]: e.target.value
                                    }))}
                                    onFocus={() => {
                                        if (!isEditing) {
                                            setEditingField(field.name);
                                        }
                                    }}
                                    onBlur={() => handleInputBlur(field, value)}
                                    onKeyDown={(e) => handleInputKeyDown(e, field, value)}
                                    placeholder={`Enter ${field.label.toLowerCase()}`}
                                    autoFocus={shouldAutoFocus || isEditing}
                                    className="flex-1 rounded-lg border border-slate-200 py-2 px-3 text-sm 
                                        text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 font-inherit"
                                    style={{
                                        '--tw-ring-color': `rgba(${hexToRgb(config.color)}, 0.1)`
                                    } as React.CSSProperties}
                                />
                                {isEditing && (
                                    <button
                                        onClick={() => {
                                            if (value) {
                                                handleFieldUpdate(field.name, value);
                                                setEditingField(null);
                                            }
                                        }}
                                        className="px-3 py-2 rounded-lg text-sm bg-white border border-slate-200 
                                            hover:bg-slate-50 transition-all duration-200 font-inherit flex items-center justify-center"
                                        style={{
                                            color: value ? `rgba(${hexToRgb(config.color)}, 0.8)` : 'rgb(156, 163, 175)',
                                            backgroundColor: value ? `rgba(${hexToRgb(config.color)}, 0.1)` : 'rgb(249, 250, 251)'
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <svg className="animate-spin h-4 w-4" 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24"
                                                style={{
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <circle className="opacity-25" cx="12" cy="12" r="10" 
                                                    stroke="currentColor" strokeWidth="4"
                                                ></circle>
                                                <path className="opacity-75" fill="currentColor" 
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                        ) : (
                                            "✓"
                                        )}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

export default FormField;